package ai.jessie.web.settings

import ai.jessie.shared.settings.SettingsChangeResponse
import ai.jessie.web.theme.ButtonVariant
import ai.jessie.web.theme.CardBodyStyle
import ai.jessie.web.theme.CardFootStyle
import ai.jessie.web.theme.CardHeadStyle
import ai.jessie.web.theme.CardStyle
import ai.jessie.web.theme.DefinitionsStyle
import ai.jessie.web.theme.HintStyle
import ai.jessie.web.theme.LabelStyle
import ai.jessie.web.theme.SubListStyle
import ai.jessie.web.theme.TableStrongStyle
import ai.jessie.web.theme.themedButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import com.varabyte.kobweb.core.AppGlobals
import com.varabyte.kobweb.core.isExporting
import com.varabyte.kobweb.silk.style.toAttrs
import kotlinx.browser.document
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.TagElement
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Ul
import org.w3c.dom.HTMLElement

// Steps 5–6: the server's exact changes and their effect, as a card grouped by the settings
// sections. Nothing is saved until the owner confirms; cancelling (A8) sends nothing.
@Composable
internal fun settingsReview(
    state: SettingsUiState,
    actions: SettingsActions,
    focusReturn: FocusReturn,
) {
    val changes = state.preview?.changes.orEmpty()
    Div(
        attrs =
            CardStyle.toAttrs {
                id(REVIEW_ID)
                attr("role", "alertdialog")
                attr("aria-labelledby", "settings-review-title")
                attr("tabindex", "-1")
            }
    ) {
        Div(attrs = CardHeadStyle.toAttrs()) {
            H2(attrs = { id("settings-review-title") }) { Text("Änderungen prüfen und bestätigen") }
        }
        Div(attrs = CardBodyStyle.toAttrs()) {
            if (changes.isEmpty())
                P(attrs = HintStyle.toAttrs()) {
                    Text("Der Vorschlag ändert nichts. Es gibt nichts zu speichern.")
                }
            else {
                changeGroups(changes)
                Ul(attrs = SubListStyle.toAttrs()) { changes.effects().forEach { Li { Text(it) } } }
            }
        }
        Div(attrs = CardFootStyle.toAttrs()) {
            themedButton("Abbrechen", actions.cancel, ButtonVariant.GHOST, disabled = state.busy)
            themedButton("Zurück zur Bearbeitung", actions.revise, disabled = state.busy)
            if (changes.isNotEmpty())
                themedButton(
                    "Änderungen speichern",
                    actions.confirm,
                    ButtonVariant.PRIMARY,
                    disabled = state.busy,
                )
        }
    }
    LaunchedEffect(state.preview) {
        focusReturn.pending = true
        if (!AppGlobals.isExporting) (document.getElementById(REVIEW_ID) as? HTMLElement)?.focus()
    }
}

// Changed values by section, in the order of the settings cards; the new value is emphasised.
@Composable
private fun changeGroups(changes: List<SettingsChangeResponse>) {
    changes
        .groupBy { sectionTitle(it.field) }
        .toList()
        .sortedBy { (title, _) -> sectionOrder.indexOf(title) }
        .forEach { (title, rows) ->
            H3(attrs = LabelStyle.toAttrs()) { Text(title) }
            TagElement<HTMLElement>("dl", DefinitionsStyle.toAttrs()) {
                rows.forEach {
                    TagElement<HTMLElement>("dt", null) { Text(fieldLabel(it.field)) }
                    TagElement<HTMLElement>("dd", null) {
                        Text("${shown(it.previous)} → ")
                        Span(attrs = TableStrongStyle.toAttrs()) { Text(shown(it.new)) }
                    }
                }
            }
        }
}

// Lazy: a plain top-level value could read `settingsSections` before its file is initialised in
// the production bundle.
private val sectionOrder by lazy {
    settingsSections.map { it.title } +
        listOf("MWST-Versionen", "Dokumentkategorien", "Dokumentordner", "Weitere")
}

// BR-177, BR-178, BR-180 and BR-181: effect on new, scheduled and retained activity.
private fun List<SettingsChangeResponse>.effects(): List<String> = buildList {
    val fields = this@effects.map { it.field }
    add(
        "Standardwerte gelten nur für neu erstellte Vorgänge. Bestehende Entwürfe ändern sich " +
            "nicht stillschweigend."
    )
    if (fields.any { it == "invoicePrefix" || it.startsWith("invoiceSequence[") })
        add(
            "Ein geändertes Rechnungspräfix und die nächste Nummer gelten nur für künftig " +
                "ausgestellte Rechnungen. Frühere Präfixe behalten ihren Nummernstand."
        )
    if (fields.any { it.startsWith("vatRate[") })
        add(
            "MWST-Versionen gelten ab ihrem Gültigkeitsdatum; das Ende der Vorgängerversion wird " +
                "automatisch angepasst. Rechnungen behalten ihren festgehaltenen Namen und Satz."
        )
    if ("timeZone" in fields)
        add(
            "Die neue Zeitzone gilt für künftige Timer-Grenzen, das lokale «Heute», Fälligkeiten " +
                "und neue Ausgaben. Abgeschlossene Intervalle und erfasste lokale Daten bleiben."
        )
    if (this@effects.any { it.field.endsWith(".active") && it.new == "false" })
        add(
            "Archivierte Kategorien und Ordner können nicht neu zugewiesen werden. Bestehende " +
                "Zuweisungen und der Verlauf bleiben erhalten."
        )
    add(
        "Ausgestellte Rechnungen, gespeicherte PDFs, historische lokale Daten und " +
            "unveränderliche Momentaufnahmen ändern sich nie."
    )
}

private const val REVIEW_ID = "settings-review"
